import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Temporary bridge removed — PDFs now served by our own backend.
export const PDF = {
  feedback360: (lang = "en") => `${API}/public/feedback-360-pdf?lang=${lang}`,
  library: (lang = "en") => `${API}/public/image-library-pdf?lang=${lang}`,
};
// kept for backwards-compat with existing imports
export const LEGACY_PDF = PDF;

const client = axios.create({ baseURL: API });
client.interceptors.request.use((config) => {
  const token = localStorage.getItem("rbl_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const imageUrl = (id) => `${API}/public/portal-image/${id}`;

export const getPortal = () => client.get("/public/portal").then((r) => r.data);
export const getImageLibrary = () => client.get("/public/image-library").then((r) => r.data);
export const getCommitments = () => client.get("/public/commitments").then((r) => r.data);
export const postCommitment = (payload) => client.post("/public/commitments", payload).then((r) => r.data);

// auth
export const login = (email, password) => client.post("/auth/login", { email, password }).then((r) => r.data);
export const fetchMe = () => client.get("/auth/me").then((r) => r.data);
export const forgotPassword = (email) => client.post("/auth/forgot-password", { email }).then((r) => r.data);
export const resetPassword = (token, password) => client.post("/auth/reset-password", { token, password }).then((r) => r.data);
export const validateMagicToken = (token) => client.post("/auth/validate-magic-token", { token }).then((r) => r.data);
export const getStats = () => client.get("/stats").then((r) => r.data);

// admin users
export const listUsers = () => client.get("/users").then((r) => r.data);
export const createUser = (payload) => client.post("/users", payload).then((r) => r.data);
export const deleteUser = (id) => client.delete(`/users/${id}`).then((r) => r.data);
export const resendWelcome = (id) => client.post(`/users/${id}/resend-welcome`).then((r) => r.data);
export const resendWelcomeBulk = (ids) => client.post("/users/resend-welcome-bulk", { user_ids: ids || null }).then((r) => r.data);

// re-export from portal helper for convenience
export { getUi, getTiles, getTile, getSessionContent } from "@/portal/portal";

// participant app
export const myWorksheets = () => client.get("/me/worksheets").then((r) => r.data);
export const getWorksheet = (id) => client.get(`/worksheets/${id}`).then((r) => r.data);
export const submitWorksheet = (id, answers) => client.post(`/worksheets/${id}/submit`, { answers }).then((r) => r.data);
export const myQuizzes = () => client.get("/me/quizzes").then((r) => r.data);
export const getQuiz = (id) => client.get(`/quizzes/${id}`).then((r) => r.data);
export const submitQuiz = (id, answers) => client.post(`/quizzes/${id}/submit`, { answers }).then((r) => r.data);
export const getReflectionPrompts = () => client.get("/reflection-prompts").then((r) => r.data);
export const myReflections = () => client.get("/me/reflections").then((r) => r.data);
export const createReflection = (payload) => client.post("/me/reflections", payload).then((r) => r.data);
export const myProgram = () => client.get("/me/program").then((r) => r.data);
export const myPart2 = () => client.get("/me/part2").then((r) => r.data);
export const myPart2Responses = (day) => client.get("/me/part2/responses", { params: { day } }).then((r) => r.data);
export const saveMyPart2Response = (b) => client.put("/me/part2/responses", b).then((r) => r.data);
export const adminPart2Responses = (day) => client.get("/admin/part2/responses", { params: { day } }).then((r) => r.data);
export const qrLogin = (token) => client.post("/auth/qr-login", { token }).then((r) => r.data);
export const adminCheckinLive = (group_id, day) => client.get("/admin/part2/checkin-live", { params: { group_id, day } }).then((r) => r.data);
export async function downloadAdminQrCards(group_id) {
  const res = await client.post("/admin/part2/qr-cards", { group_id }, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = "rbl-qr-cards.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}
export async function downloadPart2Pdf(body, filename) {
  const res = await client.post("/me/part2/pdf", body, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}
export const adminPart2 = () => client.get("/admin/part2").then((r) => r.data);
export const adminUpdatePart2 = (n, b) => client.put(`/admin/part2/${n}`, b).then((r) => r.data);

// admin back-office
export const adminParticipantProgress = (id) => client.get(`/admin/participants/${id}/progress`).then((r) => r.data);
export const adminBulkOnboard = (b) => client.post("/admin/bulk-onboard", b).then((r) => r.data);
export const adminGroups = () => client.get("/admin/groups").then((r) => r.data);
export const adminCreateGroup = (b) => client.post("/admin/groups", b).then((r) => r.data);
export const adminUpdateGroup = (id, b) => client.put(`/admin/groups/${id}`, b).then((r) => r.data);
export const adminDeleteGroup = (id) => client.delete(`/admin/groups/${id}`).then((r) => r.data);
export const adminMaterials = () => client.get("/admin/materials").then((r) => r.data);
export const adminCreateMaterial = (b) => client.post("/admin/materials", b).then((r) => r.data);
export const adminDeleteMaterial = (id) => client.delete(`/admin/materials/${id}`).then((r) => r.data);
export const adminWorksheets = () => client.get("/admin/worksheets").then((r) => r.data);
export const adminDeleteWorksheet = (id) => client.delete(`/admin/worksheets/${id}`).then((r) => r.data);
export const adminQuizzes = () => client.get("/admin/quizzes").then((r) => r.data);
export const adminDeleteQuiz = (id) => client.delete(`/admin/quizzes/${id}`).then((r) => r.data);
export const adminLive = () => client.get("/admin/live").then((r) => r.data);
export const adminReflections = () => client.get("/admin/reflections").then((r) => r.data);
export const adminAnalytics = () => client.get("/admin/analytics").then((r) => r.data);
export async function adminDownloadCohortCsv() {
  const res = await client.get("/admin/cohort-progress.csv", { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
  const a = document.createElement("a");
  const today = new Date().toISOString().slice(0, 10);
  a.href = url; a.download = `rbl_cohort_progress_${today}.csv`; document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(url);
}
export const adminListExports = () => client.get("/admin/data-export").then((r) => r.data);
export const adminCreateExport = () => client.post("/admin/data-export").then((r) => r.data);
export const adminExportUrl = (filename) => `${API}/admin/data-export/${filename}`;
export async function adminDownloadExport(filename) {
  const res = await client.get(`/admin/data-export/${filename}`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
  const a = document.createElement("a");
  a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(url);
}

export async function downloadJourneyPdf({ name, answers, extra_answers, lang }) {
  const res = await client.post("/public/journey-pdf", { name, answers, extra_answers, lang }, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = "rbl-training-journey.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

// collect all worksheet answers stored in localStorage (keyed rbl_ans_*)
export function collectLocalAnswers() {
  const answers = {};
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && k.startsWith("rbl_ans_")) answers[k.slice(8)] = localStorage.getItem(k);
  }
  return answers;
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default client;
